/**
 * Template List Component
 * Displays a list of document templates with filtering and actions
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  Search,
  Plus,
  Edit2,
  Trash2,
  Copy,
  Archive,
  RotateCcw,
  Filter,
  ChevronDown,
  Loader,
  AlertCircle,
  Tag,
  Clock,
  Users,
  Globe
} from 'lucide-react';
import { Card, CardHeader } from '../../common/Card';
import { Button } from '../../common/Button';
import { Input } from '../../common/Input';
import { useAuth } from '../../../contexts/AuthContext';
import { templateService } from '../../../services/api/documentGeneratorService';
import {
  DocumentTemplate,
  DocumentCategory,
  TemplateFilters,
  DOCUMENT_CATEGORY_LABELS
} from '../../../types/documentGenerator.types';

interface TemplateListProps {
  onSelectTemplate?: (template: DocumentTemplate) => void;
  onCreateTemplate?: () => void;
  onEditTemplate?: (template: DocumentTemplate) => void;
  selectionMode?: boolean;
}

const categoryColors: Record<DocumentCategory, string> = {
  nda: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  loa: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  loc: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
  employment: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  internship: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
  confidentiality: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
  compliance: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  delegation: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  policy: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  other: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
};

export const TemplateList: React.FC<TemplateListProps> = ({
  onSelectTemplate,
  onCreateTemplate,
  onEditTemplate,
  selectionMode = false
}) => {
  const { token } = useAuth();
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<DocumentCategory | ''>('');
  const [showArchived, setShowArchived] = useState(false);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0
  });

  const fetchTemplates = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const filters: TemplateFilters = {
        page: pagination.page,
        limit: pagination.limit,
        isActive: !showArchived
      };

      if (searchQuery) filters.search = searchQuery;
      if (categoryFilter) filters.category = categoryFilter;

      const result = await templateService.getAll(token, filters);
      setTemplates(result.data);
      setPagination(prev => ({
        ...prev,
        total: result.pagination.total,
        totalPages: result.pagination.totalPages
      }));
    } catch (err: any) {
      setError(err.message || 'Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  }, [token, pagination.page, pagination.limit, searchQuery, categoryFilter, showArchived]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleCategoryChange = (category: DocumentCategory | '') => {
    setCategoryFilter(category);
    setPagination(prev => ({ ...prev, page: 1 }));
    setShowFilterMenu(false);
  };

  const handleArchive = async (template: DocumentTemplate) => {
    if (!token) return;
    try {
      await templateService.archive(token, template._id);
      fetchTemplates();
    } catch (err: any) {
      setError(err.message || 'Failed to archive template');
    }
  };

  const handleRestore = async (template: DocumentTemplate) => {
    if (!token) return;
    try {
      await templateService.restore(token, template._id);
      fetchTemplates();
    } catch (err: any) {
      setError(err.message || 'Failed to restore template');
    }
  };

  const handleClone = async (template: DocumentTemplate) => {
    if (!token) return;
    try {
      await templateService.clone(token, template._id, `${template.name} (Copy)`);
      fetchTemplates();
    } catch (err: any) {
      setError(err.message || 'Failed to clone template');
    }
  };

  const handleDelete = async (template: DocumentTemplate) => {
    if (!token) return;
    if (!window.confirm(`Are you sure you want to delete "${template.name}"?`)) return;

    try {
      await templateService.delete(token, template._id);
      fetchTemplates();
    } catch (err: any) {
      setError(err.message || 'Failed to delete template');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-text-primary">Document Templates</h2>
          <p className="text-text-secondary mt-1">
            {pagination.total} template{pagination.total !== 1 ? 's' : ''} available
          </p>
        </div>
        {!selectionMode && onCreateTemplate && (
          <Button onClick={onCreateTemplate} className="gap-2">
            <Plus className="w-4 h-4" />
            New Template
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-tertiary" />
          <Input
            type="text"
            placeholder="Search templates..."
            value={searchQuery}
            onChange={handleSearch}
            className="pl-10"
          />
        </div>

        <div className="relative">
          <Button
            variant="secondary"
            onClick={() => setShowFilterMenu(!showFilterMenu)}
            className="gap-2"
          >
            <Filter className="w-4 h-4" />
            {categoryFilter ? DOCUMENT_CATEGORY_LABELS[categoryFilter] : 'All Categories'}
            <ChevronDown className="w-4 h-4" />
          </Button>

          {showFilterMenu && (
            <div className="absolute right-0 mt-2 w-56 bg-surface-base rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
              <button
                onClick={() => handleCategoryChange('')}
                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 ${
                  !categoryFilter ? 'bg-gray-50 dark:bg-gray-800 font-medium' : ''
                }`}
              >
                All Categories
              </button>
              {(Object.keys(DOCUMENT_CATEGORY_LABELS) as DocumentCategory[]).map(cat => (
                <button
                  key={cat}
                  onClick={() => handleCategoryChange(cat)}
                  className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 ${
                    categoryFilter === cat ? 'bg-gray-50 dark:bg-gray-800 font-medium' : ''
                  }`}
                >
                  {DOCUMENT_CATEGORY_LABELS[cat]}
                </button>
              ))}
            </div>
          )}
        </div>

        <Button
          variant={showArchived ? 'primary' : 'secondary'}
          onClick={() => {
            setShowArchived(!showArchived);
            setPagination(prev => ({ ...prev, page: 1 }));
          }}
          className="gap-2"
        >
          <Archive className="w-4 h-4" />
          {showArchived ? 'Active Only' : 'Show Archived'}
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : templates.length === 0 ? (
        <Card className="text-center py-12">
          <FileText className="w-12 h-12 mx-auto text-text-tertiary mb-4" />
          <h3 className="text-lg font-medium text-text-primary">No templates found</h3>
          <p className="text-text-secondary mt-1">
            {searchQuery || categoryFilter
              ? 'Try adjusting your search or filters'
              : 'Create your first template to get started'}
          </p>
        </Card>
      ) : (
        /* Template Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map(template => (
            <Card
              key={template._id}
              hover
              className={`cursor-pointer ${selectionMode ? 'hover:ring-2 hover:ring-primary-500' : ''}`}
              onClick={() => selectionMode && onSelectTemplate?.(template)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${categoryColors[template.category]}`}>
                      {DOCUMENT_CATEGORY_LABELS[template.category]}
                    </span>
                    {template.isArchived && (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                        Archived
                      </span>
                    )}
                  </div>
                  <h3 className="font-semibold text-text-primary truncate">{template.name}</h3>
                  <p className="text-sm text-text-secondary line-clamp-2 mt-1">
                    {template.description || 'No description'}
                  </p>
                </div>
                <FileText className="w-8 h-8 text-primary-600 flex-shrink-0" />
              </div>

              {/* Template Info */}
              <div className="flex flex-wrap gap-3 text-xs text-text-tertiary mb-4">
                <div className="flex items-center gap-1">
                  <Tag className="w-3.5 h-3.5" />
                  <span>{template.placeholders.length} placeholders</span>
                </div>
                <div className="flex items-center gap-1">
                  <Globe className="w-3.5 h-3.5" />
                  <span>{template.jurisdictionVariants.length + 1} jurisdictions</span>
                </div>
                <div className="flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" />
                  <span>{template.usageCount} uses</span>
                </div>
              </div>

              {/* Tags */}
              {template.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-4">
                  {template.tags.slice(0, 3).map(tag => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 text-text-secondary rounded"
                    >
                      {tag}
                    </span>
                  ))}
                  {template.tags.length > 3 && (
                    <span className="text-xs text-text-tertiary">+{template.tags.length - 3}</span>
                  )}
                </div>
              )}

              {/* Footer */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-1 text-xs text-text-tertiary">
                  <Clock className="w-3.5 h-3.5" />
                  <span>v{template.version}</span>
                </div>

                {!selectionMode && (
                  <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                    {onEditTemplate && (
                      <button
                        onClick={() => onEditTemplate(template)}
                        className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4 text-text-secondary" />
                      </button>
                    )}
                    <button
                      onClick={() => handleClone(template)}
                      className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                      title="Clone"
                    >
                      <Copy className="w-4 h-4 text-text-secondary" />
                    </button>
                    {template.isArchived ? (
                      <button
                        onClick={() => handleRestore(template)}
                        className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                        title="Restore"
                      >
                        <RotateCcw className="w-4 h-4 text-text-secondary" />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleArchive(template)}
                        className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                        title="Archive"
                      >
                        <Archive className="w-4 h-4 text-text-secondary" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(template)}
                      className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={pagination.page === 1}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
          >
            Previous
          </Button>
          <span className="text-sm text-text-secondary px-4">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={pagination.page === pagination.totalPages}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default TemplateList;
